/**
 * Admin permission catalogue. The database `permissions` table is synced from
 * this list at startup; it is the single source of truth for permission keys.
 */
export const PERMISSIONS = {
  INFRASTRUCTURE_VIEW: { group: "Infrastructure", description: "View regions, servers and provisioning" },
  SERVER_ADD: { group: "Infrastructure", description: "Add new servers" },
  SERVER_PROVISION: { group: "Infrastructure", description: "Start and retry server provisioning" },
  SERVER_MANAGE: { group: "Infrastructure", description: "Maintenance, reboot, diagnostics and updates" },
  SERVER_DRAIN: { group: "Infrastructure", description: "Drain servers and migrate workloads" },
  SERVER_REMOVE: { group: "Infrastructure", description: "Retire and remove servers" },
  CUSTOMER_VIEW: { group: "Customers", description: "View customers and their accounts" },
  CUSTOMER_MANAGE: { group: "Customers", description: "Create, update, suspend customers and reset access" },
  WEBSITE_VIEW: { group: "Hosting", description: "View websites and applications" },
  WEBSITE_MANAGE: { group: "Hosting", description: "Manage websites and applications" },
  DATABASE_MANAGE: { group: "Hosting", description: "Manage customer databases" },
  APP_CATALOG_MANAGE: { group: "Hosting", description: "Enable, restrict and pause App Installer applications and approve releases" },
  MIGRATION_VIEW: { group: "Migrations", description: "View customer migrations, progress and logs (never source credentials)" },
  MIGRATION_MANAGE: { group: "Migrations", description: "Cancel and retry customer migrations" },
  BACKUP_VIEW: { group: "Backups", description: "View backups and policies" },
  BACKUP_RESTORE: { group: "Backups", description: "Restore backups" },
  BACKUP_MANAGE: { group: "Backups", description: "Configure backup storage destinations" },
  MONITORING_VIEW: { group: "Monitoring", description: "View metrics, alerts, incidents and background jobs" },
  LOGS_VIEW: { group: "Monitoring", description: "View platform and server logs" },
  SECURITY_MANAGE: { group: "Security", description: "Manage admins, roles, sessions and security policy" },
  AUDIT_VIEW: { group: "Security", description: "View the audit log" },
  BILLING_MANAGE: { group: "Billing", description: "Manage plans, subscriptions and billing integrations" },
  SETTINGS_MANAGE: { group: "Settings", description: "Manage platform settings" },
} as const satisfies Record<string, { group: string; description: string }>;

export type Permission = keyof typeof PERMISSIONS;

export const ALL_PERMISSIONS = Object.keys(PERMISSIONS) as Permission[];

export function isPermission(value: string): value is Permission {
  return Object.hasOwn(PERMISSIONS, value);
}

/** Built-in roles created on first start. `Owner` is immutable and always holds every permission. */
export const SYSTEM_ROLES = {
  OWNER: {
    name: "Owner",
    description: "Full access to every HostDock capability. Cannot be edited.",
    permissions: ALL_PERMISSIONS,
  },
  OPERATOR: {
    name: "Operator",
    description: "Runs infrastructure and hosting operations.",
    permissions: [
      "INFRASTRUCTURE_VIEW", "SERVER_ADD", "SERVER_PROVISION", "SERVER_MANAGE", "SERVER_DRAIN",
      "CUSTOMER_VIEW", "WEBSITE_VIEW", "WEBSITE_MANAGE", "DATABASE_MANAGE", "APP_CATALOG_MANAGE",
      "BACKUP_VIEW", "BACKUP_RESTORE", "BACKUP_MANAGE", "MONITORING_VIEW", "LOGS_VIEW", "MIGRATION_VIEW", "MIGRATION_MANAGE",
    ] satisfies Permission[],
  },
  SUPPORT: {
    name: "Support",
    description: "Helps customers with their accounts and websites.",
    permissions: [
      "CUSTOMER_VIEW", "CUSTOMER_MANAGE", "WEBSITE_VIEW", "WEBSITE_MANAGE", "BACKUP_VIEW", "MONITORING_VIEW", "MIGRATION_VIEW",
    ] satisfies Permission[],
  },
  AUDITOR: {
    name: "Auditor",
    description: "Read-only access for compliance review.",
    permissions: [
      "INFRASTRUCTURE_VIEW", "CUSTOMER_VIEW", "WEBSITE_VIEW", "BACKUP_VIEW", "MONITORING_VIEW", "LOGS_VIEW", "AUDIT_VIEW", "MIGRATION_VIEW",
    ] satisfies Permission[],
  },
} as const;

export const OWNER_ROLE_NAME = SYSTEM_ROLES.OWNER.name;
